<!-- src/shared/components/consent/CookieConsentBanner.vue -->

<script setup lang="ts">
  /**
   * LGPD cookie / analytics consent banner (SeuID).
   *
   * A non-blocking bottom bar in the terminal house style (mono uppercase
   * label, hairline top border, dark-aware day-/night- tokens). It only renders
   * when Google Analytics is configured AND the visitor has not yet decided.
   * "Recusar" is a first-class button, exactly as prominent as "Aceitar"
   * (art. 7º/8º LGPD). All wiring lives in useCookieConsent — this component is
   * presentation + intent only.
   */
  import { useI18n } from 'vue-i18n';

  import { useCookieConsent } from '@/shared/composables/useCookieConsent';

  const { t } = useI18n();
  const { isVisible, accept, reject } = useCookieConsent();
</script>

<template>
  <Transition name="consent-fade">
    <aside
      v-if="isVisible"
      role="dialog"
      aria-modal="false"
      :aria-label="t('cookie_consent.title')"
      class="fixed inset-x-0 bottom-0 z-50 border-t border-day-line bg-white/95 backdrop-blur
        dark:border-white/[0.07] dark:bg-night/95">
      <div
        class="mx-auto flex max-w-5xl flex-col gap-4 px-5 py-5 sm:px-8
          md:flex-row md:items-center md:justify-between md:gap-8">
        <div class="min-w-0">
          <p class="mb-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-brand-600 dark:text-brand-500">
            {{ t('cookie_consent.eyebrow') }}
          </p>
          <p class="text-[13px] font-light leading-[1.6] text-day-ink-muted dark:text-night-ink-muted">
            {{ t('cookie_consent.body') }}
            <router-link
              to="/privacidade#cookies"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">
              {{ t('cookie_consent.privacy_link') }}
            </router-link>
          </p>
        </div>

        <div class="flex shrink-0 items-center gap-3">
          <button
            type="button"
            data-testid="cookie-consent-reject"
            class="rounded-sm border border-day-line px-4 py-2 font-mono text-[11px] uppercase
              tracking-[0.08em] text-day-ink transition-colors hover:bg-day-100
              dark:border-white/[0.12] dark:text-night-ink dark:hover:bg-night-100"
            @click="reject">
            {{ t('cookie_consent.reject') }}
          </button>
          <button
            type="button"
            data-testid="cookie-consent-accept"
            class="rounded-sm bg-brand-600 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.08em]
              text-white transition-colors hover:bg-brand-700"
            @click="accept">
            {{ t('cookie_consent.accept') }}
          </button>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
  .consent-fade-enter-active,
  .consent-fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .consent-fade-enter-from,
  .consent-fade-leave-to {
    opacity: 0;
    transform: translateY(0.5rem);
  }

  @media (prefers-reduced-motion: reduce) {
    .consent-fade-enter-active,
    .consent-fade-leave-active {
      transition: none;
    }
  }
</style>
